<template>
  <div class="organisation-layout">
    <div
      :class="{
        'organisation-layout__overlay': true,
        'organisation-layout__overlay--open': sidebarOpen,
      }"
      @click="sidebarOpen = false"
    />
    <OperationsSidebarSkeleton v-if="!organisations" />
    <div
      v-if="organisations?.length > 0"
      class="organisation-layout__sidebar"
    >
      <ul class="organisation-list">
        <li
          v-for="organisation in organisations"
          :key="organisation.id"
          class="organisation-list__item"
        >
          <div class="organisation-list__link-container">
            <router-link
              activeClass="organisation-list__link--active"
              class="organisation-list__link"
              :to="{
                name: 'operations',
                params: { organisationId: organisation.id },
              }"
              @click.passive="sidebarOpen = false"
            >
              <div class="organisation-list__logo">
                <OrganisationLogo :organisation="organisation" />
              </div>
              <div class="organisation-list__label typo-small-title">
                {{ organisation.name }}
              </div>
            </router-link>
          </div>
        </li>

        <li
          v-if="currentOrganisation?.policy?.new"
          class="organisation-list__item"
        >
          <div class="organisation-list__link-container">
            <router-link
              class="organisation-list__link"
              :to="{ name: 'newOrganisation' }"
              @click.passive="sidebarOpen = false"
            >
              <div class="organisation-list__logo">
                <OrganisationLogo :organisation="{ name: '+' }" />
              </div>
              <div class="organisation-list__label typo-small-title">
                {{ $t('nav.new_organisation') }}
              </div>
            </router-link>
          </div>
        </li>
      </ul>
    </div>

    <div class="organisation-layout__content">
      <div class="organisation-layout__header">
        <div class="organisation-layout__header-start">
          <div
            v-if="organisations?.length > 0"
            class="menu hide-for-medium"
          >
            <ul class="menu__items">
              <li class="menu-item">
                <button
                  class="menu-item__link menu-item__link--icon"
                  type="button"
                  @click="sidebarOpen = true"
                >
                  <div class="menu-item__label">
                    <UseSvg
                      class="icon"
                      name="list_bullet"
                    />
                  </div>
                </button>
              </li>
            </ul>
          </div>

          <template v-if="currentOrganisation?.name">
            <div class="organisation-layout__heading show-for-medium">
              <h1 class="organisation-layout__heading">
                {{ currentOrganisation?.name }}
              </h1>
              <div
                v-if="currentOrganisation"
                class="organisation-layout__sub-heading"
              >
                <template v-if="!headerInfo?.loading && headerInfo?.label">
                  {{ headerInfo?.count }}
                  {{ $t(`activerecord.models.${headerInfo?.label}`, {count: headerInfo?.count}) }}
                </template>
                <SkeletonElement v-else />
              </div>
            </div>
            <div class="show-for-medium">
              <Select
                v-if="operationOptions?.length > 0"
                v-model="operation"
                :disabled="operationOptions.length <= 1"
                :items="operationOptions"
                :modifiers="['border-left', 'min-height', 'margin-top', 'margin-left', 'label-margin-bottom', 'big-font', 'white-color']"
                placement="bottom-start"
              />
            </div>
          </template>
          <SkeletonElement
            v-else
            :height="70"
            :width="200"
          />
        </div>

        <div class="organisation-layout__header-end">
          <!-- cleanup when policy exists -->
          <UserAdminNavigation
            v-if="currentOrganisation && adminMenuItems"
            :adminMenuItems="adminMenuItems"
            :currentUser="currentUser?.full_name"
            :policy="currentOrganisation[`${headerInfo?.label}_policy`] ?? { new: true }"
          />
        </div>

        <div
          v-if="currentOrganisation"
          class="organisation-layout__header-title hide-for-medium"
        >
          <div class="organisation-layout__logo">
            <OrganisationLogo :organisation="currentOrganisation" />
          </div>
          <h1 class="organisation-layout__label">
            {{ currentOrganisation.name }}
          </h1>
          <div class="organisation-layout__sub-label typo-small-title">
            <template v-if="!headerInfo?.loading">
              {{ headerInfo?.count }}
              {{ $t(`activerecord.models.${headerInfo?.label}`, {count: headerInfo?.count}) }}
            </template>
            <Loader v-else />
          </div>
        </div>
        <div class="hide-for-medium">
          <Select
            v-if="operationOptions?.length > 0"
            v-model="operation"
            :disabled="operationOptions.length <= 1"
            :items="operationOptions"
            :modifiers="['dark-style', 'margin-top', 'margin-bottom', 'big-font', 'white-color']"
          />
        </div>
      </div>

      <router-view @update="headerInfo = $event" />

      <Loader v-if="loading" />

      <div v-if="organisations?.length === 0">
        <EmptyMessage
          :message="`${$t('auth.your_account')} (${currentUser?.email}) ${$t('auth.no_access')}`"
          :title="$t('auth.no_access_title')"
        >
          <NoEntrySvg />

          <template #actions>
            <button
              class="button button--inverse"
              type="button"
              @click="logOut(ajax)"
            >
              {{ $t('auth.log_out') }}
            </button>
          </template>
        </EmptyMessage>
      </div>
    </div>
  </div>
  <Footer />
</template>

<script setup>
import { computed, inject, nextTick, onMounted, provide, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import useAjax from '@/functions/use_ajax';
import { logOut } from '@/functions/navigation';
import isDesktop from '@/functions/responsive';

// Components
import EmptyMessage from '@/components/generic/empty_message.vue';
import Footer from '@/components/generic/footer.vue';
import Loader from '@/components/generic/loader.vue';
import OperationsSidebarSkeleton from '@/components/operations/operations_sidebar_skeleton.vue';
import OrganisationLogo from '@/components/organisations/organisation_logo.vue';
import SkeletonElement from '@/components/generic/skeleton_element.vue';
import UserAdminNavigation from '@/components/navigation/user_admin_navigation.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Select from '@/components/form/select.vue';

// Icons
import NoEntrySvg from '@/images/no-entry.svg';

const props = defineProps({
  organisationId: { type: String, default: null },
});

const router = useRouter();
const route = useRoute();
const ajax = useAjax();
const currentUser = inject('currentUser');

const headerInfo = ref(null);
const loading = ref(true);
const currentOrganisation = ref(null);
const organisations = ref(null);
const sidebarOpen = ref(false);
const operation = ref(null);
const operationOptions = ref([]);
const organisationId = ref(null);
const possibleOperationOptions = {
  plants_index: 'plants',
  shafts_index: 'shafts',
  shafts_v2_shafts_index: 'shafts_v2_shafts',
  ships_index:  'ships',
  equipment_index:  'equipment',
};

provide('currentOrganisation', currentOrganisation);

const adminMenuItems = computed(() => {
  if (!currentOrganisation.value) return null;

  let items = [];
  if (currentOrganisation.value?.policy?.manage_users) {
    items.push({
      label: I18n.t('nav.manage_users'),
      to: {
        name: 'allUsersScoped',
        params: { type: route.name === 'operation' ? 'plants' : route.name },
      },
    });
  }
  if (currentOrganisation.value?.policy?.edit) {
    items.push({
      label: I18n.t('nav.edit_organisation'),
      to: {
        name: isDesktop() ? 'editOrganisationConfiguration' : 'organisationConfiguration',
        params: { organisationId: props.organisationId },
      },
    });
  }
  return items;
});

onMounted(() => {
  loadOrganisations();
  load();
});

watch(props, loadCurrentOrganisation);

watch(operation, redirect);

watch(props, load);

async function load() {
  loading.value = true;
  await loadCurrentOrganisation();

  setPage();
}

function activeOperation(organisation) {
  if (!organisation) return;

  const activeOperationList = [];

  Object.entries(possibleOperationOptions).forEach(item => {
    if (organisation?.policy[item[0]]) {
      activeOperationList.push({ value: item[1], label: I18n.t(`nav.${item[1]}`) });
    }
  });

  return activeOperationList;
}

async function currentPage(name, useSwitch = true) {
  operation.value = null;

  await nextTick(() => operation.value = useSwitch ? switchType(name) : name);
}

function setPage() {
  const operationItem = operationOptions.value?.find((item) => item.value === switchType(route.name));

  if (operationItem) {
    currentPage(operationItem.value, false);
  }
}

function redirect(name) {
  if (!name) return;
  router.replace({
    name: name !== 'shafts_v2_shafts' && name !== 'equipment' ? name : switchType(name),
    params: { organisationId: props.organisationId ?? organisationId.value },
  });
}

function switchType(name) {
  const firstOperation = operationOptions.value?.length > 0 ? operationOptions.value[0].value : 'operations';

  const list = {
    shafts_v2: 'shafts_v2_shafts',
    equipmentList: 'equipment',
    shafts_v2_shafts: 'shafts_v2',
    equipment: 'equipmentList',
    operations: firstOperation,
    root: firstOperation
  };

  return list[name] || name;
}

async function loadCurrentOrganisation() {
  loading.value = true;
  currentOrganisation.value = null;
  if (props.organisationId) {
    let { ok, data } = await ajax(`/organisations/${props.organisationId}`);

    if (ok) {
      currentOrganisation.value = data;
      operationOptions.value = activeOperation(data);
    }
  }
  loading.value = false;
}

async function loadOrganisations() {
  let { ok, data } = await ajax('/organisations');

  if (ok) {
    organisations.value = data;

    if (!props.organisationId && organisations.value.length > 0) {
      /**
       * If props.organisationId we want to select the
       * last organisation the user had selected,
       * else we select the first available organisation
       * they have access to.
       */
      organisationId.value = currentUser.last_organisation_id || organisations.value[0].id;
      const activeOrganisation = organisations.value.find(organisation => organisationId.value === organisation.id);
      operationOptions.value = activeOperation(activeOrganisation);
      currentPage(operationOptions.value[0]?.value);
    } else {
      /**
       * Verify if current operation exists for this organisation,
       * else select the first available operation for the organisation
       */
      const availableRoute = operationOptions.value.find(operation => switchType(route?.name) === operation.value);
      !availableRoute && currentPage(operationOptions.value[0]?.value, false);
    }
  } else {
    organisations.value = [];
  }
}
</script>
